import React, { useEffect } from "react";
import { Link, Redirect, useParams } from "react-router-dom";
import styled from "styled-components";
import { getAdjacent, getNewsletter, getRelated } from "../../content/newsletters";
import { formatDate } from "../../content/newsletters/format";
import {
  categoryAccent,
  categoryLabel,
  getCategory,
} from "../../content/newsletters/categories";
import { categoryPath } from "../../components/Newsletter/CategoryNav";
import PostLink from "../../components/Newsletter/PostLink";
import NotionBlocks from "../../components/Newsletter/NotionBlocks";
import SubscribeForm from "../../components/Newsletter/SubscribeForm";
import { Page, TextLink, Wrap } from "../../components/Newsletter/theme";

const TEXT = {
  ko: {
    backToList: "뉴스레터",
    newer: "다음 글",
    older: "이전 글",
    more: (name) => `${name} 소식 더 보기`,
    seeAll: "전체 보기",
    breadcrumb: "현재 위치",
    siteName: "바삭한소프트",
  },
  en: {
    backToList: "Newsletter",
    newer: "Newer",
    older: "Older",
    more: (name) => `More from ${name}`,
    seeAll: "See all",
    breadcrumb: "Breadcrumb",
    siteName: "Basakansoft",
  },
};

function NewsletterPost() {
  const { lang, slug } = useParams();
  const language = lang === "en" ? "en" : "ko";
  const t = TEXT[language];
  const post = getNewsletter(slug, language);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | ${t.siteName}`;
    }
  }, [post, t.siteName]);

  if (!post) {
    return <Redirect to={`/${language}/newsletter`} />;
  }

  const { newer, older } = getAdjacent(post.slug, post.lang);
  const Body = post.Body;
  const category = getCategory(post.category);
  const label = category ? categoryLabel(category, language) : null;

  // 이전·다음 글에 이미 걸린 글은 빼서 같은 링크가 두 번 나오지 않게 한다.
  const adjacentSlugs = [newer, older].filter(Boolean).map((other) => other.slug);
  const related = getRelated(post).filter(
    (other) => !adjacentSlugs.includes(other.slug)
  );

  // 게임 카테고리 글은 링크·강조색을 그 게임 색으로. 회사소식은 지면 기본색 그대로.
  const pageStyle =
    category && category.game ? { "--nl-accent": categoryAccent(category) } : undefined;

  return (
    <Page style={pageStyle}>
      <Wrap>
        <TopNav aria-label={t.breadcrumb}>
          <BackLink to={`/${language}/newsletter`}>{t.backToList}</BackLink>
          {category ? (
            <>
              <Crumb aria-hidden="true">/</Crumb>
              <BackLink to={categoryPath(language, category.id)}>{label}</BackLink>
            </>
          ) : null}
        </TopNav>

        <Header>
          <Filed>
            <time dateTime={post.date}>{formatDate(post.date, post.lang)}</time>
            {post.tags && post.tags.length > 0 ? (
              <span>{post.tags.join(", ")}</span>
            ) : null}
            {post.author ? <span>{post.author}</span> : null}
          </Filed>
          <Title>{post.title}</Title>
          {post.summary ? <Summary>{post.summary}</Summary> : null}
        </Header>

        {post.cover ? <Cover src={post.cover} alt="" /> : null}

        <Article>
          {post.source === "notion" ? (
            <NotionBlocks blocks={post.blocks} />
          ) : (
            <Body />
          )}
        </Article>

        <SubscribeForm lang={language} source={`newsletter:${post.slug}`} />

        {related.length > 0 ? (
          <Related>
            <RelatedHead>
              <RelatedTitle>{t.more(label)}</RelatedTitle>
              <BackLink to={categoryPath(language, category.id)}>{t.seeAll}</BackLink>
            </RelatedHead>
            <RelatedList>
              {related.map((other) => (
                <PostLink
                  key={other.slug}
                  post={other}
                  lang={language}
                  showCategory={false}
                />
              ))}
            </RelatedList>
          </Related>
        ) : null}

        {newer || older ? (
          <Adjacent>
            {older ? (
              <AdjacentLink to={`/${language}/newsletter/${older.slug}`}>
                <AdjacentLabel>{t.older}</AdjacentLabel>
                <AdjacentTitle>{older.title}</AdjacentTitle>
              </AdjacentLink>
            ) : (
              <span />
            )}
            {newer ? (
              <AdjacentLink
                to={`/${language}/newsletter/${newer.slug}`}
                $alignEnd
              >
                <AdjacentLabel>{t.newer}</AdjacentLabel>
                <AdjacentTitle>{newer.title}</AdjacentTitle>
              </AdjacentLink>
            ) : (
              <span />
            )}
          </Adjacent>
        ) : null}
      </Wrap>
    </Page>
  );
}

const TopNav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem;
  padding: 2.5rem 0 0;
`;

const BackLink = styled(TextLink).attrs({ as: Link })``;

const Crumb = styled.span`
  font-size: 0.875rem;
  color: var(--nl-ink-mute);
`;

const Related = styled.section`
  margin-top: 3rem;
`;

const RelatedHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

/* 마지막 행 아랫선은 바로 밑 이전·다음 글의 윗선이 대신한다. */
const RelatedList = styled.div`
  > :last-child {
    border-bottom: 0;
  }
`;

const RelatedTitle = styled.h2`
  font-size: 1.0625rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--nl-ink);
`;

const Header = styled.header`
  padding: 2.5rem 0 2.25rem;
`;

const Filed = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.875rem;
  font-size: 0.8125rem;
  color: var(--nl-ink-mute);
  font-variant-numeric: tabular-nums;
`;

const Title = styled.h1`
  font-size: 2.375rem;
  font-weight: 800;
  line-height: 1.28;
  letter-spacing: -0.035em;
  color: var(--nl-ink);

  @media screen and (max-width: 768px) {
    font-size: 1.875rem;
  }
`;

const Summary = styled.p`
  margin-top: 1rem;
  font-size: 1.0625rem;
  line-height: 1.75;
  color: var(--nl-ink-soft);
`;

const Cover = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border-radius: 12px;
`;

const Article = styled.article`
  padding: 1.5rem 0 0;
  border-top: 1px solid var(--nl-line);
`;

const Adjacent = styled.nav`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-top: 3rem;
  padding: 2rem 0 3.5rem;
  border-top: 1px solid var(--nl-line);

  @media screen and (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`;

const AdjacentTitle = styled.span`
  display: block;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.6;
  color: var(--nl-ink);
  transition: color 0.18s ease;
`;

const AdjacentLink = styled(Link)`
  display: block;
  text-decoration: none;
  text-align: ${({ $alignEnd }) => ($alignEnd ? "right" : "left")};

  &:hover ${AdjacentTitle} {
    color: var(--nl-accent);
  }

  &:focus-visible {
    outline: 2px solid var(--nl-accent);
    outline-offset: 4px;
    border-radius: 2px;
  }

  @media screen and (max-width: 600px) {
    text-align: left;
  }
`;

const AdjacentLabel = styled.span`
  display: block;
  margin-bottom: 0.3125rem;
  font-size: 0.75rem;
  color: var(--nl-ink-mute);
`;

export default NewsletterPost;
